import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { AlertCircle, X } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Modal de confirmación para rechazar un RL. Estado terminal: el cliente
 * deberá iniciar un trámite nuevo si quiere reintentar. Motivo obligatorio
 * (CHECK declarativo en BD).
 */
export function RechazarRLModal({
  open,
  rlId,
  onClose,
  onDone,
}: {
  open: boolean;
  rlId: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [motivo, setMotivo] = useState("");
  const [error, setError] = useState<string | null>(null);

  const rechazar = useMutation({
    mutationFn: async () => {
      if (motivo.trim().length === 0) {
        throw new Error("El motivo es obligatorio.");
      }
      await api.post(`/api/registros-legales/${rlId}/rechazar`, { motivo: motivo.trim() });
    },
    onSuccess: () => {
      onDone();
      onClose();
    },
    onError: (e: unknown) => {
      const r = e as { response?: { data?: { detail?: string; title?: string } }; message?: string };
      setError(r.response?.data?.detail || r.response?.data?.title || r.message || "Error");
    },
  });

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg overflow-hidden rounded-lg bg-surface shadow-overlay">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3 className="text-sm font-semibold text-fg">Rechazar Registro Legal</h3>
          <button type="button" onClick={onClose} className="rounded p-1 hover:bg-surface-2" aria-label="Cerrar">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-3 p-4">
          <div className="rounded border border-estado-err/25 bg-estado-err-bg p-3 text-sm text-estado-err">
            <strong>Esta acción es terminal.</strong> El cliente no podrá subsanar; deberá iniciar un trámite
            nuevo si quiere reintentar.
          </div>

          <div>
            <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-fg-subtle">Motivo *</div>
            <textarea
              rows={4}
              required
              maxLength={2000}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Explica al cliente por qué el trámite no procede."
              className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
            />
            <div className="mt-1 text-right text-xs text-fg-subtle">{motivo.length}/2000</div>
          </div>

          {error && (
            <div className="flex items-start gap-2 rounded-lg border bg-estado-warn-bg p-3 text-sm text-estado-warn">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t bg-surface-2 px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            disabled={rechazar.isPending}
            className="rounded border px-3 py-1.5 text-sm text-fg-muted hover:bg-surface-2 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => rechazar.mutate()}
            disabled={rechazar.isPending}
            className="rounded bg-action-err px-4 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            {rechazar.isPending ? "Rechazando…" : "Rechazar definitivamente"}
          </button>
        </div>
      </div>
    </div>
  );
}
